import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  type TextFieldProps,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import { forwardRef, type ReactNode, useCallback, useState } from 'react'
import { themeColors } from '../../app/layout/theme'
import { validate, type ValidationRule, fieldRules } from '../../lib/validation'

// ── Field-type shorthand ──────────────────────────────────────────────────────

export type FieldType =
  | 'text'
  | 'name'
  | 'phone'
  | 'email'
  | 'password'
  | 'pincode'
  | 'pan'
  | 'aadhar'
  | 'gstin'
  | 'ifsc'
  | 'url'
  | 'number'
  | 'decimal'

/** Maps a FieldType → its default set of rules. */
function getBuiltInRules(
  fieldType: FieldType | undefined,
  required: boolean,
): ValidationRule[] {
  if (!fieldType || fieldType === 'text' || fieldType === 'number' || fieldType === 'decimal')
    return []
  const opts = { required }
  switch (fieldType) {
    case 'name':     return fieldRules.name(opts)
    case 'phone':    return fieldRules.phone(opts)
    case 'email':    return fieldRules.email(opts)
    case 'password': return fieldRules.password(opts)
    case 'pincode':  return fieldRules.pincode(opts)
    case 'pan':      return fieldRules.pan(opts)
    case 'aadhar':   return fieldRules.aadhar(opts)
    case 'gstin':    return fieldRules.gstin(opts)
    case 'ifsc':     return fieldRules.ifsc(opts)
    case 'url':      return fieldRules.url(opts)
    default:         return []
  }
}

/** Returns sensible inputMode / maxLength defaults per fieldType. */
function getInputDefaults(fieldType: FieldType | undefined) {
  switch (fieldType) {
    case 'phone':   return { inputMode: 'numeric' as const, maxLength: 10 }
    case 'pincode': return { inputMode: 'numeric' as const, maxLength: 6 }
    case 'aadhar':  return { inputMode: 'numeric' as const, maxLength: 12 }
    case 'pan':     return { maxLength: 10 }
    case 'gstin':   return { maxLength: 15 }
    case 'ifsc':    return { maxLength: 11 }
    case 'number':  return { inputMode: 'numeric' as const }
    case 'decimal': return { inputMode: 'decimal' as const }
    default:        return {}
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export type FormInputLabelPosition = 'above' | 'floating'

export interface FormInputProps extends Omit<TextFieldProps, 'variant'> {
  labelPosition?: FormInputLabelPosition

  // ── Validation ──────────────────────────────────────────────────────────────
  /**
   * Shorthand: picks the built-in rule set for the field type.
   * Combined with any extra `rules` you provide.
   */
  fieldType?: FieldType

  /**
   * Additional (or override) validation rules applied after `fieldType` rules.
   * Use `rules` from '../../lib/validation' to build them.
   */
  rules?: ValidationRule[]

  /**
   * Validate while the user types (default: validate on blur only).
   */
  validateOnChange?: boolean

  /**
   * Show a character counter below the field.
   * Requires `inputProps.maxLength` or will show current length only.
   */
  showCharCount?: boolean

  /**
   * Show a green check / red X icon inside the field when validating.
   */
  showValidationIcon?: boolean

  // ── Layout ──────────────────────────────────────────────────────────────────
  startIcon?: ReactNode
  endIcon?: ReactNode
  variant?: 'outlined' | 'standard' | 'filled'
}

// ── Component ─────────────────────────────────────────────────────────────────

export const FormInput = forwardRef<HTMLDivElement, FormInputProps>(
  (
    {
      label,
      required = false,
      labelPosition = 'floating',
      startIcon,
      endIcon,
      type,
      error: externalError,
      helperText: externalHelperText,
      size = 'small',
      variant = 'outlined',
      sx,
      InputProps,
      inputProps: externalInputProps,
      fieldType,
      rules: extraRules = [],
      validateOnChange = false,
      showCharCount = false,
      showValidationIcon = false,
      onChange,
      onBlur,
      value,
      ...rest
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const [internalError, setInternalError] = useState('')
    const [touched, setTouched] = useState(false)

    const isPassword = type === 'password' || fieldType === 'password'
    const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type
    const isAbove = labelPosition === 'above'

    // Merge built-in rules from fieldType + any extra rules passed by caller
    const allRules = [...getBuiltInRules(fieldType, required), ...extraRules]
    const hasOwnValidation = allRules.length > 0

    // Respect externally-controlled error state; use internal only when needed
    const isControlledError = externalError !== undefined
    const showError = isControlledError ? externalError : (touched && !!internalError)
    const displayHelperText = isControlledError
      ? externalHelperText
      : (touched && internalError) || externalHelperText

    // ── Validation runner ──────────────────────────────────────────────────────
    const runValidation = useCallback(
      (rawValue: string) => {
        if (!hasOwnValidation) return ''
        const err = validate(rawValue, allRules)
        setInternalError(err)
        return err
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [allRules.map(r => r.message).join('|'), hasOwnValidation],
    )

    // ── Event handlers ────────────────────────────────────────────────────────
    const handleChange: TextFieldProps['onChange'] = (e) => {
      const numericTypes = ['phone', 'pincode', 'aadhar', 'number']
      if (fieldType && numericTypes.includes(fieldType)) {
        e.target.value = e.target.value.replace(/\D/g, '')
      }

      if (fieldType === 'gstin') {
        e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
      }

      if (!isControlledError && validateOnChange && touched) {
        runValidation(e.target.value)
      }
      onChange?.(e)
    }

    const handleBlur: TextFieldProps['onBlur'] = (e) => {
      if (!isControlledError) {
        setTouched(true)
        runValidation(e.target.value)
      }
      onBlur?.(e)
    }

    // ── Input defaults per field type ─────────────────────────────────────────
    const builtInInputDefaults = getInputDefaults(fieldType)
    const mergedInputProps = { ...builtInInputDefaults, ...externalInputProps }
    const maxLength: number | undefined = mergedInputProps.maxLength

    // ── Char count ────────────────────────────────────────────────────────────
    const currentLength = typeof value === 'string' ? value.length : 0
    const charCountText = showCharCount
      ? maxLength
        ? `${currentLength} / ${maxLength}`
        : `${currentLength}`
      : undefined

    // ── Validation icon ───────────────────────────────────────────────────────
    const validationIconNode =
      showValidationIcon && touched && hasOwnValidation
        ? showError
          ? <ErrorIcon sx={{ fontSize: 16, color: themeColors.error }} />
          : <ValidIcon sx={{ fontSize: 16, color: '#16a34a' }} />
        : null

    // ── End adornment (validation icon → custom endIcon → password toggle) ────
    let resolvedEndAdornment: ReactNode = null

    if (isPassword) {
      resolvedEndAdornment = (
        <InputAdornment position="end">
          {validationIconNode && (
            <Box component="span" sx={{ display: 'flex', mr: 0.5 }}>
              {validationIconNode}
            </Box>
          )}
          <IconButton
            size="small"
            edge="end"
            tabIndex={-1}
            onClick={() => setShowPassword(v => !v)}
          >
            {showPassword
              ? <VisibilityOff sx={{ fontSize: 18 }} />
              : <Visibility    sx={{ fontSize: 18 }} />}
          </IconButton>
        </InputAdornment>
      )
    } else if (validationIconNode || endIcon) {
      resolvedEndAdornment = (
        <InputAdornment position="end">
          {validationIconNode}
          {endIcon && <Box component="span" sx={{ display: 'flex' }}>{endIcon}</Box>}
        </InputAdornment>
      )
    }

    // ── Start adornment ───────────────────────────────────────────────────────
    const resolvedStartAdornment = startIcon
      ? <InputAdornment position="start">{startIcon}</InputAdornment>
      : undefined

    const mergedMuiInputProps: TextFieldProps['InputProps'] = {
      ...InputProps,
      ...(resolvedStartAdornment ? { startAdornment: resolvedStartAdornment } : {}),
      ...(resolvedEndAdornment   ? { endAdornment:   resolvedEndAdornment   } : {}),
    }

    const hasInputProps = Object.keys(mergedMuiInputProps).length > 0

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <Box ref={ref} sx={{ width: '100%', ...sx }}>
        {/* Above label */}
        {isAbove && label && (
          <Typography
            component="label"
            sx={{
              display: 'block',
              fontSize: '12px',
              fontWeight: 600,
              color: showError ? themeColors.error : themeColors.text.primary,
              mb: 0.5,
              userSelect: 'none',
              lineHeight: 1.4,
            }}
          >
            {label}
            {required && (
              <Box component="span" sx={{ color: themeColors.error, ml: '2px' }}>*</Box>
            )}
          </Typography>
        )}

        <TextField
          label={isAbove ? undefined : label}
          required={!isAbove && required}
          size={size}
          fullWidth
          variant={variant}
          type={resolvedType}
          error={showError}
          helperText={displayHelperText}
          InputProps={hasInputProps ? mergedMuiInputProps : InputProps}
          inputProps={mergedInputProps}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          {...rest}
        />

        {/* Character count */}
        {charCountText && (
          <Typography
            fontSize="10.5px"
            color={showError ? 'error' : 'text.disabled'}
            textAlign="right"
            mt={0.25}
          >
            {charCountText}
          </Typography>
        )}
      </Box>
    )
  },
)

FormInput.displayName = 'FormInput'
